import { datadogRum } from '@datadog/browser-rum';

import type { EnvSettings } from '@/constants/constants';
import { FF_DATADOG_SESSION_REPLAY_SAMPLE_RATE } from '@/constants/featureFlags';
import ffForceSessionReplays from '@/constants/ffForceSessionReplays';
import { Logger } from '@/utils/logger';
import { viteAppEnv } from '@montugroup/circuit-shared/constants/env';

const logger = new Logger('datadog');

export const hasFFSessionReplayOverride = (
  flags: Record<string, any>,
  flagsToOverrideSessionReplays: Record<string, boolean | any[]>,
) => {
  // We only want to opt-in to replays for feature flags if we are on production - these flags may also be enabled on staging but to reduce costs we won't force these to become recorded sessions
  if (viteAppEnv() !== 'production') {
    return false;
  }

  // Find the first feature flag that exists in our force session replays config
  const matchingFlag = Object.keys(flags).find((key) => flagsToOverrideSessionReplays[key]);

  if (!matchingFlag) {
    return false;
  }

  const flagValue = flags[matchingFlag];
  const forceReplayConfig = flagsToOverrideSessionReplays[matchingFlag];

  // Handle boolean flags
  if (typeof flagValue === 'boolean') {
    return flagValue;
  }

  // Handle array-based flags (e.g. A/B test variations)
  if (Array.isArray(forceReplayConfig) && typeof flagValue === 'string') {
    return forceReplayConfig.includes(flagValue);
  }

  return false;
};

export default function init(flags: Record<string, any>, settings: EnvSettings) {
  const { datadog } = settings;

  // Force session replay sample rate to 100% if the feature flag has been added to force session replays
  const sampleRateFFOverride = hasFFSessionReplayOverride(flags, ffForceSessionReplays)
    ? 100
    : flags[FF_DATADOG_SESSION_REPLAY_SAMPLE_RATE];

  if (datadog) {
    logger.debug('Initializing DataDog RUM');

    datadogRum.init({
      ...datadog,
      applicationId: '2a7ec7af-8bd2-403e-bca9-618f1d3c3a38',
      clientToken: 'pubbc48eee0cbeb1f3ac5db6d8bd99c0f3b',
      service: 'patient-portal',
      site: 'us3.datadoghq.com',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      enableExperimentalFeatures: ['feature_flags'],
      sessionReplaySampleRate: sampleRateFFOverride,
    });

    datadogRum.startSessionReplayRecording();
  }
}
